import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { MasaSiparisi } from '../types';
import { PackageKitchenReceiptData, usePackageKitchenPrint } from '../components/PackageKitchenPrintService';
import { PackageReceiptData, printPackageReceipt } from '../components/PackageReceiptPrintService';
import { resolveReceiptHeader } from '../utils/receiptHeader';
import type { PlatformIntegration } from '../../../../types/platformIntegration';

// ============================================================================
// QR ORDER DETAIL MODAL - QR Menü / E-Ticaret / Kiosk Sipariş Detay
// ============================================================================

export interface QrOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  order: MasaSiparisi | null;
  onUpdateStatus: (orderId: number, status: string) => Promise<boolean>;
  onRefreshOrders: () => Promise<void>;
  companyId?: string | null;
  /**
   * Online ödenmiş QR/E-Ticaret/Kiosk siparisleri için Pavo cihazından mali fis
   * kestirme handler'i. Tanimliysa ve siparis online odenmis ise "Online Fis
   * Yazdir" butonu gorunur.
   */
  onRequestOnlineReceipt?: () => void;
  screenData?: {
    id?: number;
    localServiceUrl?: string | null;
    printingSettings?: {
      printingMode?: string;
      defaultReceiptPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        thankYouMessage?: string;
        standardFooterNote?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
      };
    } | null;
    /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
    platformIntegrations?: PlatformIntegration[];
  } | null;
}

const QrOrderDetailModal: React.FC<QrOrderDetailModalProps> = ({
  open,
  onClose,
  order,
  onUpdateStatus,
  onRequestOnlineReceipt,
  onRefreshOrders,
  companyId,
  screenData,
}) => {
  const { printPackageKitchenReceipt } = usePackageKitchenPrint({
    companyId: companyId || null,
    screenId: screenData?.id?.toString() || null,
    screenSettings: screenData?.printingSettings,
    localServiceUrl: screenData?.localServiceUrl || undefined,
  });
  
  // Sipariş yoksa gösterme
  if (!order) return null;
  
  // Sipariş kaynağını belirle
  const isQrOrder = order.siparisId?.includes('-QR-');
  const isKioskOrder = order.siparisId?.includes('-KIOSK-') || 
    order.siparisId?.startsWith('D-') || order.siparisId?.startsWith('P-') ||
    (order.siparisId ? /[DP]-\d+$/.test(order.siparisId) : false) ||
    (order.siparisKaynagi || '').toLowerCase().includes('kiosk');
  
  // Kiosk kısa kodu çıkar (eski ve yeni format desteği)
  const kioskShortCode = order.siparisId?.match(/[DP]-\d+$/)?.[0] || '';
  
  const getOrderTitle = () => {
    if (isQrOrder) return 'QR Menü Siparişi';
    if (isKioskOrder) {
      if (kioskShortCode.startsWith('D-') || order.siparisId?.startsWith('D-')) return 'Kiosk - Burada Yiyecek';
      if (kioskShortCode.startsWith('P-') || order.siparisId?.startsWith('P-')) return 'Kiosk - Paket Sipariş';
      return 'Kiosk Siparişi';
    }
    return 'E-Ticaret Siparişi';
  };
  
  const getOrderIcon = () => {
    if (isQrOrder) return <QrCode2Icon sx={{ fontSize: 28 }} />;
    if (isKioskOrder) return <PointOfSaleIcon sx={{ fontSize: 28 }} />;
    return <ShoppingBagIcon sx={{ fontSize: 28 }} />;
  };
  
  const getHeaderColor = () => {
    if (isQrOrder) return 'info.main';
    if (isKioskOrder) return 'warning.main';
    return 'success.main';
  };
  
  const getOrderNo = () => {
    // Kiosk siparişleri: eski format "D-20260213-103207-296D-2" → "D-2", yeni format "P-1" → "P-1"
    if (isKioskOrder && order.siparisId) {
      const match = order.siparisId.match(/[DP]-\d+$/);
      if (match) return match[0];
    }
    if (order.siparisId?.startsWith('D-') || order.siparisId?.startsWith('P-')) {
      const match = order.siparisId.match(/[DP]-\d+$/);
      if (match) return match[0];
      return order.siparisId;
    }
    return order.siparisId?.split('-').slice(-2).join('-') || order.siparisId || '';
  };
  
  const getMasaInfo = () => {
    return order.masaAdi || (order.masaNumarasi ? `Masa ${order.masaNumarasi}` : '');
  };
  
  // Mutfağa yazdır
  const handleKitchenPrint = () => {
    const masaInfo = getMasaInfo();
    const orderNo = getOrderNo();
    
    const printData: PackageKitchenReceiptData = {
      orderNumber: masaInfo ? `${masaInfo} - ${orderNo}` : orderNo,
      shortCode: orderNo,
      orderTime: new Date(order.siparisOlusturmaTarihi).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      customerName: `Masa Siparişi - ${order.musteriAdi || order.kullaniciAdi || 'Anonim'}`,
      customerAddress: masaInfo || undefined,
      items: order.siparisDetaylari?.map((item, index) => ({
        id: item.urunId || `qr-item-${index}`,
        name: item.urunAdi,
        quantity: item.miktar || item.adet || 1,
        note: item.notlar || item.aciklama,
        category: item.kategoriAdi || item.categoryId,
      })) || [],
      orderNote: order.siparisNotu,
    };
    
    printPackageKitchenReceipt(printData);
  };
  
  // Fiş yazdır
  const handleReceiptPrint = async () => {
    if (!screenData?.localServiceUrl) {
      alert('Yazdırma ayarları yapılandırılmamış');
      return;
    }
    
    const masaInfo = getMasaInfo();
    const orderNo = getOrderNo();
    const qrHeader = resolveReceiptHeader(null, screenData);

    const receiptData: PackageReceiptData = {
      orderNumber: masaInfo ? `${masaInfo} - ${orderNo}` : orderNo,
      shortCode: orderNo,
      date: new Date(order.siparisOlusturmaTarihi).toLocaleDateString('tr-TR'),
      time: new Date(order.siparisOlusturmaTarihi).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      customerName: `Masa Siparişi - ${order.musteriAdi || order.kullaniciAdi || 'Anonim'}`,
      customerAddress: masaInfo || undefined,
      items: order.siparisDetaylari?.map(item => ({
        name: item.urunAdi,
        quantity: item.miktar || item.adet || 1,
        unitPrice: item.birimFiyat || 0,
        totalPrice: item.toplamFiyat || 0,
      })) || [],
      subtotal: order.toplamTutar || 0,
      vatTotal: 0,
      grandTotal: order.toplamTutar || 0,
      orderNote: order.siparisNotu,
      // QR/kiosk siparişinin markası yok; çözümleyici ekran varsayılanını
      // döndürür ve başlık bugünküyle aynı basılır.
      ...qrHeader,
      logoMaxWidthPx: qrHeader.logoMaxWidthPx ?? 384,
      logoMaxHeightPx: qrHeader.logoMaxHeightPx ?? 150,
    };
    
    const printer = screenData.printingSettings?.defaultReceiptPrinter;
    if (printer) {
      await printPackageReceipt(receiptData, printer, screenData.localServiceUrl);
    } else {
      alert('Varsayılan yazıcı ayarlanmamış');
    }
  };
  
  // Hazır durumuna güncelle
  const handleMarkReady = async () => {
    await onUpdateStatus(order.id, 'Hazır');
  };
  
  // Teslim edildi durumuna güncelle
  const handleMarkDelivered = async () => {
    await onUpdateStatus(order.id, 'Teslim Edildi');
    onClose();
    await onRefreshOrders();
  };
  
  const isDelivered = order.siparisDurumu?.toLowerCase() === 'teslim edildi';
  const isReady = order.siparisDurumu?.toLowerCase() === 'hazır';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
    >
      {/* Başlık */}
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: getHeaderColor(),
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getOrderIcon()}
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {getOrderTitle()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              #{getOrderNo()}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Masa ve Müşteri Bilgileri */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {/* Masa Bilgisi */}
          {getMasaInfo() && (
            <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 200, bgcolor: 'info.light' }}>
              <Typography variant="caption" color="info.dark" fontWeight="bold">
                🪑 MASA
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="info.dark">
                {getMasaInfo()}
              </Typography>
            </Paper>
          )}
          
          {/* Sipariş Zamanı */}
          <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              🕐 SİPARİŞ ZAMANI
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {new Date(order.siparisOlusturmaTarihi).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(order.siparisOlusturmaTarihi).toLocaleDateString('tr-TR')}
            </Typography>
          </Paper>

          {/* Durum */}
          <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              📊 DURUM
            </Typography>
            <Chip 
              label={order.siparisDurumu || 'Bekliyor'} 
              color={
                isReady ? 'success' :
                order.siparisDurumu?.toLowerCase() === 'hazırlanıyor' ? 'warning' :
                isDelivered ? 'info' : 'default'
              }
              sx={{ mt: 0.5 }}
            />
          </Paper>
        </Box>

        {/* Müşteri Bilgileri */}
        {(order.musteriAdi || order.kullaniciAdi) && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              👤 Müşteri Bilgileri
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {order.musteriAdi || order.kullaniciAdi}
            </Typography>
            {order.musteriTelefon && (
              <Typography variant="body2" color="text.secondary">
                📞 {order.musteriTelefon}
              </Typography>
            )}
            {order.musteriAdres && (
              <Typography variant="body2" color="text.secondary">
                📍 {order.musteriAdres}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Sipariş Kalemleri */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          🛒 Sipariş Kalemleri ({order.siparisDetaylari?.length || 0} ürün)
        </Typography>
        
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {order.siparisDetaylari?.map((item, index) => (
            <Paper 
              key={index} 
              elevation={1} 
              sx={{ 
                p: 2, 
                mb: 1.5, 
                display: 'flex', 
                gap: 2,
                alignItems: 'center',
              }}
            >
              {/* Ürün Görseli */}
              {item.urunResmi && (
                <Box
                  component="img"
                  src={item.urunResmi}
                  alt={item.urunAdi}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                  onError={(e: any) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              
              {/* Ürün Bilgileri */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  {item.urunAdi}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.kategoriAdi || item.categoryId}
                </Typography>
                {(item.notlar || item.aciklama) && (
                  <Typography variant="caption" color="warning.main">
                    📝 {item.notlar || item.aciklama}
                  </Typography>
                )}
              </Box>
              
              {/* Miktar ve Fiyat */}
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  {item.miktar || item.adet || 1} x {item.birimFiyat?.toFixed(2) || '0.00'} ₺
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {item.toplamFiyat?.toFixed(2) || '0.00'} ₺
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Sipariş Notu */}
        {order.siparisNotu && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark" fontWeight="medium">
              📝 Sipariş Notu: {order.siparisNotu}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Toplam */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6">TOPLAM</Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {order.toplamTutar?.toFixed(2) || '0.00'} ₺
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        {/* Sol taraf - Yazdır Butonları */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={handleKitchenPrint}
          >
            Mutfağa Yazdır
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handleReceiptPrint}
          >
            Fiş Yazdır
          </Button>

          {/* Online ödenmiş web sitesi / e-ticaret / QR siparisleri icin
              REST-P2P uzerinden Pavo cihazindan mali fis kestirme. Web sitesi
              (e-commerce) siparisleri default online odenmis sayilir; QR/Kiosk
              icin de online tahsilat yapilmissa kullanici basabilir. */}
          {onRequestOnlineReceipt && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={onRequestOnlineReceipt}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Online Fiş Yazdır
            </Button>
          )}
        </Box>

        {/* Sağ taraf - Durum Butonları */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isDelivered && (
            <>
              {!isReady && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleMarkReady}
                >
                  ✅ Hazır
                </Button>
              )}
              <Button
                variant="contained"
                color="info"
                onClick={handleMarkDelivered}
              >
                📦 Teslim Et
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={onClose}>
            Kapat
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QrOrderDetailModal;
